import { useEffect, useMemo, useState } from "react";
import { COMMISSION_LEVEL_OPTIONS, compensationTypeLabel } from "@aam/shared";
import { getSessionToken } from "../hooks/useSession";
import { Button } from "./ui/Button";
import { Card, CardDescription, CardHeader, CardTitle } from "./ui/Card";
import { FieldHelp, Input, Label, Select } from "./ui/Input";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

const PRESET_VALUES = new Set(COMMISSION_LEVEL_OPTIONS.map((option) => option.value));

interface AgentProfileProvisioningProps {
  agentId: string;
  initial: {
    commissionLevel: string | null;
  };
  /** Commission level from the agent's latest application, if any. */
  applicationCommissionLevel?: string | null;
  /** Advance or as earned, from the invite the agent joined through. */
  compensationType?: string | null;
}

export function AgentProfileProvisioning({
  agentId,
  initial,
  applicationCommissionLevel,
  compensationType,
}: AgentProfileProvisioningProps) {
  const token = getSessionToken();
  const compensationLabel = compensationTypeLabel(compensationType);
  const savedLevel = initial.commissionLevel?.trim() ?? "";
  const [commissionLevel, setCommissionLevel] = useState(savedLevel);
  const [useCustom, setUseCustom] = useState(
    Boolean(savedLevel && !PRESET_VALUES.has(savedLevel))
  );
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    const next = initial.commissionLevel?.trim() ?? "";
    setCommissionLevel(next);
    setUseCustom(Boolean(next && !PRESET_VALUES.has(next)));
    setMessage(null);
  }, [agentId, initial.commissionLevel]);

  const isDirty = commissionLevel.trim() !== savedLevel;
  const applicationLevel = applicationCommissionLevel?.trim() || null;
  const showApplicationHint =
    applicationLevel &&
    applicationLevel !== commissionLevel.trim() &&
    applicationLevel !== savedLevel;

  const selectValue = useMemo(() => {
    if (useCustom) return "__custom__";
    if (commissionLevel && PRESET_VALUES.has(commissionLevel)) return commissionLevel;
    return "";
  }, [commissionLevel, useCustom]);

  async function save() {
    if (!token || !isDirty) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/profile`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          commissionLevel: commissionLevel.trim() || null,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to update profile");
      setMessage({ type: "success", text: "Commission level saved." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to save",
      });
    } finally {
      setSaving(false);
    }
  }

  function applyApplicationLevel() {
    if (!applicationLevel) return;
    if (PRESET_VALUES.has(applicationLevel)) {
      setUseCustom(false);
    } else {
      setUseCustom(true);
    }
    setCommissionLevel(applicationLevel);
    setMessage(null);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Agent account</CardTitle>
        <CardDescription>
          Portal access is automatic. Commission level drives referral link defaults and downline signup.
        </CardDescription>
      </CardHeader>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,280px)_1fr] lg:items-end">
        <div className="space-y-3">
          <Label>
            Commission level
            {useCustom ? (
              <Input
                className="mt-1.5"
                value={commissionLevel}
                onChange={(e) => {
                  setCommissionLevel(e.target.value);
                  setMessage(null);
                }}
                placeholder="e.g. Level 11 - 80.00%"
              />
            ) : (
              <Select
                className="mt-1.5"
                value={selectValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "__custom__") {
                    setUseCustom(true);
                    setCommissionLevel("");
                  } else {
                    setUseCustom(false);
                    setCommissionLevel(value);
                  }
                  setMessage(null);
                }}
              >
                <option value="">Not set</option>
                {COMMISSION_LEVEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                <option value="__custom__">Custom value…</option>
              </Select>
            )}
          </Label>
          {useCustom && (
            <button
              type="button"
              onClick={() => {
                setUseCustom(false);
                setCommissionLevel(PRESET_VALUES.has(savedLevel) ? savedLevel : "");
                setMessage(null);
              }}
              className="text-xs text-accent hover:opacity-90"
            >
              Use preset levels
            </button>
          )}
          <FieldHelp>
            {savedLevel
              ? `Currently saved: ${savedLevel}`
              : "No commission level saved yet."}
            {" "}
            {compensationLabel
              ? `${compensationLabel}, set on the invite.`
              : "Advance or as earned was not set on the invite."}
          </FieldHelp>
        </div>

        {applicationLevel && (
          <div className="rounded-lg border border-border-subtle bg-surface-input px-3 py-2.5 text-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-content-muted">
              On application
            </p>
            <p className="mt-1 font-medium text-content-primary">{applicationLevel}</p>
            {showApplicationHint && (
              <button
                type="button"
                onClick={applyApplicationLevel}
                className="mt-2 text-xs text-accent hover:opacity-90"
              >
                Apply application value
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={save} disabled={saving || !isDirty} size="sm">
          {saving ? "Saving…" : "Save commission level"}
        </Button>
        {isDirty && (
          <button
            type="button"
            onClick={() => {
              setCommissionLevel(savedLevel);
              setUseCustom(Boolean(savedLevel && !PRESET_VALUES.has(savedLevel)));
              setMessage(null);
            }}
            className="text-xs text-content-muted hover:text-content-primary"
          >
            Reset
          </button>
        )}
        {message && (
          <p
            className={`text-xs ${
              message.type === "success" ? "text-status-active-text" : "text-status-rejected-text"
            }`}
          >
            {message.text}
          </p>
        )}
      </div>
    </Card>
  );
}
